import { computed, defineComponent, h, onUnmounted, ref, watchEffect } from 'vue';

import {
  classesStates,
  classNames,
  getCurrentInstance,
  warn,
  wrapperRaf,
  addEventListenerWrapper,
  supportsPassive
} from '@components/utils';

import { useInjectTableContext } from '../../context';

import type { NTable } from '../../interface';
import type { PropType } from 'vue';

type TEventHandler = (...args: any[]) => void;
type THandleEvent = MouseEvent & TouchEvent;

const events = {
  mouse: {
    start: 'mousedown',
    move: 'mousemove',
    stop: 'mouseup'
  },
  touch: {
    start: 'touchstart',
    move: 'touchmove',
    stop: 'touchend'
  }
};

const defaultMinWidth = 50;

export default defineComponent({
  name: 'DragHandle',
  props: {
    width: {
      type: Number,
      required: true
    },
    minWidth: {
      type: Number,
      default: defaultMinWidth
    },
    maxWidth: {
      type: Number,
      default: Infinity
    },
    column: {
      type: Object as PropType<NTable.NColumn.IColumn>,
      default: undefined as unknown as NTable.NColumn.IColumn
    }
  },
  setup(props) {
    let startX = 0;
    let moveEvent = { remove: () => {} };
    let stopEvent = { remove: () => {} };

    const removeEvents = () => {
      moveEvent.remove();
      stopEvent.remove();
    };

    onUnmounted(() => {
      removeEvents();
    });

    watchEffect(() => {
      if (isNaN(props.width)) {
        warn('CdsTable', 'width must be a number when use resizable');
      }
    });

    const { onResizeColumn } = useInjectTableContext();
    const minWidth = computed(() =>
      typeof props.minWidth === 'number' && !isNaN(props.minWidth) ? props.minWidth : defaultMinWidth
    );
    const maxWidth = computed(() =>
      typeof props.maxWidth === 'number' && !isNaN(props.maxWidth) ? props.maxWidth : Infinity
    );

    const instance = getCurrentInstance('drag-handle');
    const dragging = ref(false);

    let baseWidth = 0;
    let rafId: number;

    const updateWidth = (event: THandleEvent) => {
      let pageX = 0;

      if (event.touches) {
        if (event.touches.length) {
          pageX = event.touches[0].pageX;
        } else {
          pageX = event.changedTouches[0].pageX;
        }
      } else {
        pageX = event.pageX;
      }

      const tmpDeltaX = startX - pageX;

      let w = Math.max(baseWidth - tmpDeltaX, minWidth.value);
      w = Math.min(w, maxWidth.value);

      wrapperRaf.cancel(rafId);

      rafId = wrapperRaf(() => {
        onResizeColumn(w, props.column.__originColumn__);
      });
    };

    const handleMove = (event: THandleEvent) => {
      updateWidth(event);
    };

    const handleStop = (event: THandleEvent) => {
      dragging.value = false;

      updateWidth(event);
      removeEvents();
    };

    const handleStart = (event: THandleEvent, eventsFor: any) => {
      dragging.value = true;

      removeEvents();

      baseWidth = instance?.vnode?.el?.parentNode.getBoundingClientRect().width;

      if (event instanceof MouseEvent && event.which !== 1) return;
      if (event.stopPropagation) event.stopPropagation();

      startX = event.touches ? event.touches[0].pageX : event.pageX;
      moveEvent = addEventListenerWrapper(document.documentElement, eventsFor.move, handleMove);
      stopEvent = addEventListenerWrapper(document.documentElement, eventsFor.stop, handleStop);
    };

    const handleDown: TEventHandler = (event: THandleEvent) => {
      event.stopPropagation();
      event.preventDefault();

      handleStart(event, events.mouse);
    };

    const handleTouchDown: TEventHandler = (event: THandleEvent) => {
      event.stopPropagation();
      event.preventDefault();

      handleStart(event, events.touch);
    };

    const handleClick: TEventHandler = (event: THandleEvent) => {
      event.stopPropagation();
      event.preventDefault();
    };

    return () => {
      const touchEvents = {
        [supportsPassive ? 'onTouchstartPassive' : 'onTouchstart']: (event: THandleEvent) => handleTouchDown(event)
      };

      return h(
        'div',
        {
          class: classNames('cds-table__resize-handle', { [classesStates.dragging]: dragging.value }),
          onMousedown: handleDown,
          ...touchEvents,
          onClick: handleClick
        },
        h('div', { class: 'cds-table__resize-handle-line' })
      );
    };
  }
});
